import { Injectable } from '@angular/core';
import { filter, map } from 'rxjs/operators';
import { Store } from '../store';
import { Socket } from 'ngx-socket-io';
import { environment as env } from '../../environments/environment';
import { ApiAuthHttpClient } from '../http/ApiAuthHttpClient';
import { ChatEvent, ChatEventsName, ChatMessage } from '../data-types/chat-data';
import { Subject, take } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ChatService {
    domain = this.store.selectForLocal('domain');
    user = this.store.selectForLocal('user');

    private chatEvent$ = new Subject<ChatEvent>();

    private chatRooms = ['sycamore'];

    constructor(private store: Store, private socket: Socket, private http: ApiAuthHttpClient) {
        let joined = false;
        this.store
            .select<any>('user')
            .pipe(
                filter((user) => user),
                take(1)
            )
            .subscribe((user) => {
                if (joined) {
                    this.joinRoom(user.id);
                } else {
                    this.chatRooms.push(user.id);
                }
            });

        this.socket.on('connect', () => {
            joined = true;
            for (const room of this.chatRooms) {
                this.joinRoom(room);
            }
        });

        this.socket
            .fromEvent('didMessageEdit')
            .subscribe((message: ChatMessage) => this.chatEvent$.next({ event: ChatEventsName.OnEdit, data: message }));
        this.socket
            .fromEvent('didMessageDelete')
            .subscribe((message: string) => this.chatEvent$.next({ event: ChatEventsName.OnDelete, data: message }));
    }

    sendTypingSignal(data) {
        this.socket.emit('userTyping', data);
    }

    receiveTypingSignal() {
        this.socket.fromEvent('userTyping').subscribe((data: any) => {
            const chatStore = this.store.selectForLocal('chatStore');
            if (chatStore.group && chatStore.group.groupId === data.groupId) {
                chatStore.group.typing = data;
            }
            this.store.set('chatStore', chatStore);
        });
    }

    reEnterRooms() {
        for (const room of this.chatRooms) {
            this.leaveRoom(room);
        }
        this.domain = this.store.selectForLocal('domain');
        for (const room of this.chatRooms) {
            this.joinRoom(room);
        }
    }

    sendOnlineSignal() {
        const user = this.store.selectForLocal('user');

        if (user) {
            this.socket.emit('userOnlineStatus', { id: user.id, online: true });
        }
    }

    joinRoom(roomName: string) {
        if (this.chatRooms.indexOf(roomName) === -1) {
            // initial join
            this.chatRooms.push(roomName);
        }

        this.socket.emit('join', { domain: this.domain, room: roomName });
    }

    leaveRoom(roomName: string) {
        if (this.chatRooms.indexOf(roomName) !== -1) {
            // removing from the list
            this.chatRooms.splice(this.chatRooms.indexOf(roomName), 1);
        }

        this.socket.emit('leave', { domain: this.domain, room: roomName });
    }

    sendOfflineSignal() {
        const user = this.store.selectForLocal('user');
        this.socket.emit('userOnlineStatus', { id: user.id, online: false });
    }

    receiveUserOnlineStatus() {
        this.socket.fromEvent('onlineUsers').subscribe((userData: any) => {
            const onlineUsers = this.store.selectForLocal('onlineUsers');
            if (userData.online && onlineUsers.indexOf(userData.userId) < 0) {
                onlineUsers.push(userData.userId);
            }
            if (!userData.online && onlineUsers.indexOf(userData.userId) >= 0) {
                onlineUsers.splice(onlineUsers.indexOf(userData.userId), 1);
            }
            this.store.set('onlineUsers', onlineUsers);
        });
    }

    getUsers() {
        return this.socket.fromEvent('users');
    }

    getByGroupId(groupId) {
        return this.http.get(env.apiPath + 'chat/' + groupId).pipe(map((data: any) => data));
    }
}
